import {
  Box,
  Button,
  Card,
  CardHeader,
  IconButton,
  ListItemAvatar,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactNode, useMemo } from 'react';

import { AddressModel } from '@common-lib';
import { DynamicIcon } from '@frontend-libs/atomic';

import { tPortalLib } from '../../locales';
import { type PortalTheme } from '../../theme';

interface ExternalLink {
  id: string;
  linkId?: string;
  title: string;
  url: string;
  ariaLabel: string;
}

export interface HomeSiteUnitCardProps {
  displayName: string;
  descriptionVisibility?: 'show' | 'hide';
  addressVisibility?: 'streetCity' | 'hide';
  description?: string;
  address?: AddressModel | undefined;
  thumbnail?: ReactNode | undefined;
  onClick?: () => void;
  externalLinks?: ExternalLink[] | undefined;
  onClickExternalLink?: (url: string, linkId: string | undefined) => void;
  shopId?: string | undefined;
  onClickOrder?: (shopId: string) => void;
  orderActionLabel?: string;
  orderDisabled?: boolean;
  menuShopId?: string;
  menuActionTitle?: string;
  onClickMenu?: (siteUnitId: string) => void;
}

export const HomeSiteUnitCard = (props: HomeSiteUnitCardProps) => {
  const theme = useTheme<PortalTheme>();

  const formattedAddress = useMemo(() => {
    return AddressModel.formatAddress(props.address);
  }, [props.address]);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: `${theme.shape.borderRadius}px`,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.boxShadow,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <CardHeader
        onClick={props.onClick}
        avatar={
          props.thumbnail && (
            <Box sx={{ marginBottom: 'auto' }}>
              <ListItemAvatar sx={{ borderRadius: '4px' }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    marginRight: 1,
                    borderRadius: '4px',
                  }}
                >
                  {props.thumbnail}
                </Box>
              </ListItemAvatar>
            </Box>
          )
        }
        action={
          <IconButton>
            <DynamicIcon
              iconId="info"
              strokeColor={theme.palette.text.primary}
            />
          </IconButton>
        }
        sx={{
          p: 1.5,
          cursor: 'pointer',
        }}
        slotProps={{
          avatar: {
            sx: {
              marginRight: 1.5,
            },
          },
          action: {
            sx: {
              pr: 1,
            },
          },
        }}
        title={<Typography variant="subtitle1">{props.displayName}</Typography>}
        subheader={
          <Box sx={{ pt: 0.5 }}>
            {props.descriptionVisibility !== 'hide' ? (
              <Typography variant="body2">{props.description}</Typography>
            ) : undefined}

            {props.addressVisibility !== 'hide' && formattedAddress ? (
              <Typography variant="body2">{formattedAddress}</Typography>
            ) : undefined}
          </Box>
        }
      />

      <Box
        sx={{
          p: 1.5,
          pt: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {props.externalLinks?.map((link) => (
          <Button
            key={link.id}
            fullWidth
            size="medium"
            color="secondary"
            variant="contained"
            component={link.url ? 'a' : 'button'}
            aria-label={link.ariaLabel}
            sx={{ textTransform: 'none' }}
            {...(link.url && {
              target: '_blank',
              rel: 'noopener noreferrer',
              href: link.url,
            })}
            onClick={(
              e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
            ) => {
              e.stopPropagation();

              if (link.url) {
                e.preventDefault();
              }
              props.onClickExternalLink?.(link.url, link.linkId);
            }}
          >
            {link.title}
          </Button>
        ))}

        {props.menuShopId && props.onClickMenu && (
          <Button
            fullWidth
            size="large"
            color="primary"
            variant="contained"
            onClick={() => {
              props.onClickMenu?.(props.menuShopId!);
            }}
          >
            {props.menuActionTitle}
          </Button>
        )}

        {props.shopId && props.onClickOrder && (
          <Button
            fullWidth
            size="large"
            color="primary"
            variant="contained"
            disabled={props.orderDisabled}
            onClick={(e) => {
              e.stopPropagation();
              props.onClickOrder?.(props.shopId!);
            }}
          >
            {props.orderActionLabel || tPortalLib.siteUnits.orderNow()}
          </Button>
        )}
      </Box>
    </Card>
  );
};
